/**
 * Parent view-model for company certificate trees.
 *
 * Card = company_certificates. Issuance = company_cert_records.
 * current_record is selected start_of_validity DESC, id DESC (never created_at/updated_at).
 *
 * Missing API field vs explicit null vs empty string vs empty files[] are distinct.
 */

const ISSUANCE_OWNED_FIELDS = new Set([
  'id',
  'certificate_id',
  'start_of_validity',
  'end_of_validity',
  'description',
  'created_at',
  'updated_at',
  'created_by_user',
  'createdByUser',
  'updated_by_user',
  'updatedByUser',
  'created_by_user_id',
  'updated_by_user_id',
  'files',
]);

export function compareCertRecordsByCurrentSelector(a, b) {
  const aStart = certRecordStartKey(a);
  const bStart = certRecordStartKey(b);
  if (aStart !== bStart) {
    return aStart < bStart ? 1 : -1;
  }
  return certRecordIdKey(b) - certRecordIdKey(a);
}

export function selectCurrentCertRecordFromChildren(children) {
  const list = Array.isArray(children) ? children.slice() : [];
  if (list.length === 0) {
    return null;
  }
  list.sort(compareCertRecordsByCurrentSelector);
  return list[0] ?? null;
}

export function isCompanyCertificateParentRow(row) {
  return row != null && Object.prototype.hasOwnProperty.call(row, '_children');
}

/**
 * Explicit `current_record: null` means no issuance.
 * Missing key (older payload) computes from `_children` with the same selector.
 */
export function getCurrentCertRecord(parent) {
  if (!parent || typeof parent !== 'object') {
    return null;
  }
  if (Object.prototype.hasOwnProperty.call(parent, 'current_record')) {
    return parent.current_record;
  }
  return selectCurrentCertRecordFromChildren(parent._children);
}

export function hasCurrentCertRecord(parent) {
  return getCurrentCertRecord(parent) != null;
}

/** Catalog "Popis" = card description only. Children have no catalog description field. */
export function resolveCatalogDescription(row) {
  if (isCompanyCertificateParentRow(row)) {
    return row?.description ?? '';
  }
  return '';
}

/**
 * "Popis certifikátu" = issuance description when an issuance exists (including '').
 * No issuance → empty. Never copies card description.
 */
export function resolveIssuanceDescription(row) {
  if (isCompanyCertificateParentRow(row)) {
    const current = getCurrentCertRecord(row);
    if (current == null) {
      return '';
    }
    return current.description ?? '';
  }
  return row?.description ?? '';
}

export function resolveIssuanceStartOfValidity(row) {
  if (isCompanyCertificateParentRow(row)) {
    const current = getCurrentCertRecord(row);
    if (current == null) {
      return null;
    }
    return current.start_of_validity ?? null;
  }
  return row?.start_of_validity ?? null;
}

export function resolveIssuanceEndOfValidity(row) {
  if (isCompanyCertificateParentRow(row)) {
    const current = getCurrentCertRecord(row);
    if (current == null) {
      return null;
    }
    return current.end_of_validity ?? null;
  }
  return row?.end_of_validity ?? null;
}

export function resolveIssuanceFiles(row) {
  if (isCompanyCertificateParentRow(row)) {
    const current = getCurrentCertRecord(row);
    if (current == null) {
      return [];
    }
    return Array.isArray(current.files) ? current.files : [];
  }
  return Array.isArray(row?.files) ? row.files : [];
}

export function resolveCardDeactivationDate(row) {
  if (isCompanyCertificateParentRow(row)) {
    return row?.deactivation_date ?? null;
  }
  return null;
}

export function resolveCardResponsibleName(row) {
  if (isCompanyCertificateParentRow(row)) {
    return row?.responsible?.name ?? '—';
  }
  return '—';
}

function calendarDateKey(value) {
  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    const y = value.getFullYear();
    const m = String(value.getMonth() + 1).padStart(2, '0');
    const d = String(value.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  }
  const s = String(value ?? '');
  if (/^\d{4}-\d{2}-\d{2}/.test(s)) {
    return s.slice(0, 10);
  }
  const parsed = new Date(s);
  if (!Number.isNaN(parsed.getTime())) {
    return calendarDateKey(parsed);
  }
  return '';
}

/**
 * Stav (OK/NOK) from issuance validity date, not card deactivation.
 * Parent uses current_record.end_of_validity; child uses its own end_of_validity.
 * Compare calendar dates (platnosť do is a date). The end day is still OK.
 */
export function resolveIssuanceStateLabel(row, now = new Date()) {
  const end = resolveIssuanceEndOfValidity(row);
  if (end == null || end === '') {
    return 'NOK';
  }
  const endKey = calendarDateKey(end);
  const nowKey = calendarDateKey(now);
  if (!endKey || !nowKey) {
    return 'NOK';
  }
  return endKey < nowKey ? 'NOK' : 'OK';
}

/**
 * Audit source for a tree row.
 * Parent with current_record → that issuance (null user stays null).
 * Parent without issuance → card audit (no issuance exists).
 * Child → own record.
 */
export function resolveCertificateTreeAuditRow(rowData) {
  if (isCompanyCertificateParentRow(rowData)) {
    const current = getCurrentCertRecord(rowData);
    if (current != null) {
      return current;
    }
    return rowData;
  }
  return rowData;
}

export function issuanceOwnsField(field) {
  return ISSUANCE_OWNED_FIELDS.has(field);
}

/**
 * Header filter values for remote query tokens.
 * Prefer column.getHeaderFilterValue so dateUserHeaderFilter range/clear survive
 * when table.getHeaderFilters() is stale or empty.
 */
export function readTabulatorHeaderFilterEntries(table) {
  if (!table) {
    return [];
  }
  const fields = new Set();
  for (const col of table.getColumns?.() ?? []) {
    const field = typeof col?.getField === 'function' ? col.getField() : null;
    if (field) {
      fields.add(field);
    }
  }
  const listed = table.getHeaderFilters?.() ?? [];
  for (const entry of listed) {
    if (entry?.field) {
      fields.add(entry.field);
    }
  }
  const listedByField = new Map();
  for (const entry of listed) {
    if (entry?.field) {
      listedByField.set(entry.field, entry.value);
    }
  }
  const out = [];
  for (const field of fields) {
    let fromColumn;
    try {
      fromColumn = table.getColumn?.(field)?.getHeaderFilterValue?.();
    } catch (_) {
      fromColumn = undefined;
    }
    const raw = fromColumn !== undefined ? fromColumn : listedByField.get(field);
    out.push({ field, value: raw });
  }
  return out;
}

export function resolveIssuanceExpirationBucket(row, now = new Date()) {
  if (isCompanyCertificateParentRow(row) && resolveCardDeactivationDate(row)) {
    return 'DEA';
  }
  const end = resolveIssuanceEndOfValidity(row);
  if (end == null || end === '') {
    return '0-';
  }
  const endKey = calendarDateKey(end);
  const nowKey = calendarDateKey(now);
  if (!endKey || !nowKey) {
    return '0-';
  }
  if (endKey < nowKey) {
    return '0-';
  }
  const addDays = (key, n) => {
    const d = new Date(`${key}T00:00:00`);
    d.setDate(d.getDate() + n);
    return calendarDateKey(d);
  };
  if (endKey <= addDays(nowKey, 15)) return '15-';
  if (endKey <= addDays(nowKey, 30)) return '30-';
  if (endKey <= addDays(nowKey, 45)) return '45-';
  if (endKey <= addDays(nowKey, 60)) return '60-';
  return '61+';
}

export function matchesTrainingCompanyCertPreview(row, timePeriod, now = new Date()) {
  const bucket = resolveIssuanceExpirationBucket(row, now);
  if (timePeriod === 'after') {
    return bucket === '0-' || bucket === 'DEA';
  }
  if (timePeriod === '15') {
    return bucket === '15-';
  }
  if (timePeriod === '30') {
    return bucket === '30-';
  }
  if (timePeriod === 'others') {
    return bucket === '45-' || bucket === '60-' || bucket === '61+';
  }
  return false;
}

function certRecordStartKey(row) {
  const raw = row?.start_of_validity;
  if (raw == null || raw === '') {
    return '';
  }
  const s = String(raw);
  return s.length >= 10 ? s.slice(0, 10) : s;
}

function certRecordIdKey(row) {
  const n = Number(row?.id);
  return Number.isFinite(n) ? n : 0;
}
